import type { ListenMeta, RtdbClient } from '@akpl/firebase/client';

import { devMembers, devMeta, devQueue, pid } from '../dev/placeholders';

/**
 * In-memory stand-in for the RTDB, used in **offline-auth** mode (docs/04 §1: `/config` has no
 * Firebase project) so the post-auth screens still render and the live wiring is exercised in
 * browser dev. It is never reachable with a real Firebase project: `getLiveClient()` prefers
 * the SDK client whenever `initFirebase` succeeded.
 *
 * Only what the TV listens to is modelled: exact-path subscriptions plus notification of the
 * descendants of a written node (so writing `player` wakes a `player/command` listener).
 */

export interface FakeLive extends RtdbClient {
  /** Writes a node and notifies its listeners (dev overlay / tests drive the feed with it). */
  emit(path: string, value: unknown): void;
  setConnected(connected: boolean): void;
  /** Every path written so far (assertions in tests). */
  readonly writes: { path: string; value: unknown }[];
}

/** RTDB ignores a leading slash; `sessions/{sid}` is collapsed so any session id matches. */
function normalize(path: string): string {
  return path.replace(/^\/+|\/+$/g, '').replace(/^sessions\/[^/]+/, 'sessions/*');
}

function childValue(value: unknown, segments: string[]): unknown {
  let current: unknown = value;
  for (const segment of segments) {
    if (current === null || typeof current !== 'object') return null;
    current = (current as Record<string, unknown>)[segment] ?? null;
  }
  return current;
}

export function createFakeLive(seed: Record<string, unknown> = defaultSeed()): FakeLive {
  const nodes = new Map<string, unknown>(Object.entries(seed).map(([k, v]) => [normalize(k), v]));
  const listeners = new Map<string, Set<(value: unknown) => void>>();
  const connectedListeners = new Set<(connected: boolean) => void>();
  let connected = true;
  const writes: { path: string; value: unknown }[] = [];

  function notify(path: string, value: unknown): void {
    for (const [key, set] of listeners) {
      if (key === path) {
        for (const cb of set) cb(value);
      } else if (key.startsWith(`${path}/`)) {
        const child = childValue(value, key.slice(path.length + 1).split('/'));
        for (const cb of set) cb(child);
      }
    }
  }

  function emit(path: string, value: unknown): void {
    const key = normalize(path);
    nodes.set(key, value);
    notify(key, value);
  }

  const live: FakeLive = {
    writes,
    emit,
    setConnected(next) {
      connected = next;
      for (const cb of connectedListeners) cb(next);
    },
    listen(path, schema, cb) {
      const key = normalize(path);
      const deliver = (raw: unknown) => {
        const meta: ListenMeta = { path, raw };
        if (raw === null || raw === undefined) {
          cb(null, { path, raw: null });
          return;
        }
        const parsed = schema.safeParse(raw);
        if (parsed.success) cb(parsed.data, meta);
        else cb(null, { ...meta, error: parsed.error });
      };
      const set = listeners.get(key) ?? new Set();
      set.add(deliver);
      listeners.set(key, set);
      // Resolve through the closest stored ancestor so `player/command` works with a `player` seed.
      let raw: unknown = nodes.get(key) ?? null;
      if (raw === null) {
        const segments = key.split('/');
        for (let i = segments.length - 1; i > 0 && raw === null; i -= 1) {
          const ancestor = nodes.get(segments.slice(0, i).join('/'));
          if (ancestor !== undefined) raw = childValue(ancestor, segments.slice(i));
        }
      }
      deliver(raw);
      return () => {
        set.delete(deliver);
        if (set.size === 0) listeners.delete(key);
      };
    },
    onConnected(cb) {
      connectedListeners.add(cb);
      cb(connected);
      return () => connectedListeners.delete(cb);
    },
    async write(path, value) {
      writes.push({ path, value });
      emit(path, value);
    },
    async patch(path, value) {
      writes.push({ path, value });
      const key = normalize(path);
      const current = nodes.get(key);
      const merged =
        current && typeof current === 'object'
          ? { ...(current as Record<string, unknown>), ...value }
          : value;
      emit(path, merged);
    },
    async onDisconnectSet() {
      // No connection to drop: the fake feed is always "online".
      return async () => {};
    },
  };
  return live;
}

/** The Figma placeholder session, so the offline screens show the same data as the frames. */
export function defaultSeed(): Record<string, unknown> {
  return {
    'sessions/*/meta': devMeta,
    'sessions/*/members': devMembers,
    'sessions/*/queue': devQueue,
    'sessions/*/player': { state: 'playing', currentItemId: pid(603) },
  };
}
